import { HeaderRoot } from '../Card/VenueCardContainerStyles'
import EventHeader from './EventHeader'
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Link,
  styled,
} from '@mui/material'
import { DateIcon, GuestsIcon, MinusIcon } from './Icons'
import { PlusIcon } from '../../../Components/Icons'
import EventDetails from './EventDetails'
import { LinkChip } from '../Chips'
import { EventInformationFragment } from './Drawer.generated'
import { useTimeSinceNow } from '../../../Utilities/useTimeSinceNow'

export const CustomRoot = styled(HeaderRoot)({
  borderRadius: '0 0 8px 8px',
  marginBottom: '2rem',
})

export const CustomAccordion = styled(Accordion)({
  boxShadow: 'none',
  '&:before': {
    content: 'none',
  },
})

export const CustonAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  padding: 0,
  minHeight: '1.5rem',
  '& .MuiAccordionSummary-content': {
    alignItems: 'center',
    flexWrap: 'wrap',
    maxWidth: '100%',
    margin: '10px auto 0 0',
    [theme.breakpoints.up(710)]: {
      maxWidth: '40%',
    },
    [theme.breakpoints.up(930)]: {
      maxWidth: '60%',
    },
    gap: '0.5rem 1.5rem',
    '&:hover': {
      cursor: 'default',
    },
  },
}))

export const CustonAccordionDetails = styled(AccordionDetails)({
  marginTop: '1.5rem',
  padding: 0,
})

const DropDown = ({
  open,
  eventInformation,
  handleClick,
  direct,
}: {
  open: boolean
  eventInformation: EventInformationFragment
  handleClick: (event: React.MouseEvent<HTMLElement>) => void
  direct: boolean
}): JSX.Element => {
  const {
    budget,
    collection,
    currency,
    eventDateFrom,
    guestsCount,
    location,
    radius,
    title,
    audience,
  } = eventInformation ?? {}

  const { medDate } = useTimeSinceNow(eventDateFrom)

  if (!location) throw new Error('no location information')
  if (!radius) throw new Error('no radius information')
  // Sentry error, default to GBP
  if (!currency) throw new Error('no currency information')
  if (!guestsCount) throw new Error('no guest count information')
  if (!eventDateFrom || !medDate) throw new Error('no time added')
  if (budget === undefined) throw new Error('no budget information')
  if (!title) throw new Error('no event title')

  return (
    <>
      <EventHeader {...{ title, audience, collection }} />
      <CustomAccordion disableGutters={true} expanded={open}>
        <CustonAccordionSummary>
          <Chip
            variant='subtle'
            size='medium'
            icon={<DateIcon titleAccess='Event date' />}
            label={medDate}
          />
          <Chip
            variant='subtle'
            size='medium'
            icon={<GuestsIcon />}
            label={`${guestsCount} guests`}
          />
          <Link variant='anchor' component='button' onClick={handleClick}>
            <LinkChip
              icon={open ? <MinusIcon /> : <PlusIcon />}
              label={open ? 'Less details' : 'More details'}
            />
          </Link>
        </CustonAccordionSummary>
        <CustonAccordionDetails>
          <EventDetails {...eventInformation} isDirectEnquiry={direct} />
        </CustonAccordionDetails>
        <Link variant='anchor' component='button' onClick={handleClick}>
          <LinkChip icon={<MinusIcon />} label={'Close details'} />
        </Link>
      </CustomAccordion>
    </>
  )
}

const EventInformationDrawer = ({
  open,
  setOpen,
  eventInformation,
  direct,
}: {
  open: boolean
  setOpen: (state: boolean) => void
  eventInformation: EventInformationFragment
  direct: boolean
}): JSX.Element | null => {
  return (
    <CustomRoot>
      <DropDown
        open={open}
        eventInformation={eventInformation}
        direct={direct}
        handleClick={() => setOpen(!open)}
      />
    </CustomRoot>
  )
}

export default EventInformationDrawer
